import { useCallback, useEffect, useRef, useState } from "react";
import { getCustomerCbdOrder } from "@/api/new-api/cbd/cbd";
import { FaUpRightAndDownLeftFromCenter } from "react-icons/fa6";
import { TbArrowsDiagonalMinimize2 } from "react-icons/tb";
import PusherHandler from "@/api/lib/pusher/PusherHandler";
import useAuth from "@/hooks/useAuth";
import avatarUser from "@/assets/images/avtar-user.png";
import { incomingCallSound } from "@/assets";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setIsLoading, setOrderData } from "@/redux/slice/take-order-slice/order-slice";
import { getSingleOrderApi, repeatOrderApi } from "@/api/take-order/take-order";
import toast from "react-hot-toast";
import { setSelectedCustomer } from "@/redux/slice/take-order-slice/selected-customer-slice";
import { setDeliveryInfo } from "@/redux/slice/take-order-slice/delivery-slice";
import useSettings from "@/hooks/useSettings";

const Call = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { callNotificationSound = true, volume } = useSelector(
        (state) => state.utility.soundNotification || {},
    );
    const callNotificationSoundRef = useRef(callNotificationSound);
    const volumeRef = useRef(volume);

    useEffect(() => {
        callNotificationSoundRef.current = callNotificationSound;
    }, [callNotificationSound]);

    useEffect(() => {
        volumeRef.current = volume;
    }, [volume]);

    const [callsQueue, setCallsQueue] = useState([]);
    const [callStates, setCallStates] = useState({}); // Track state for each call by ID
    const incomingAudioRef = useRef(null);

    const {
        connectionStatus: tableStatus,
        addEventHandler,
        removeEventHandler,
        isConnected: tableConnected,
    } = PusherHandler();

    const { user } = useAuth();
    const { settings } = useSettings();
    const currentDeviceId = Number(settings?.take_order?.device_id ?? settings?.device_id);
    const restaurantId = user?.restaurant_id || localStorage.getItem("restaurant_system_id") || "";

    const shouldShowCallForThisDevice = useCallback((incomingData) => {
        const tillIds = incomingData?.till_ids;
        if (!Array.isArray(tillIds) || tillIds.length === 0) return true;
        if (!Number.isFinite(currentDeviceId)) return false;
        return tillIds.map((id) => Number(id)).includes(currentDeviceId);
    }, [currentDeviceId]);

    // Initialize call state
    const initializeCallState = (callId) => {
        setCallStates((prev) => {
            if (prev[callId]) return prev;
            return {
                ...prev,
                [callId]: {
                    showCallNotification: true,
                    isMinimized: false,
                    recentOrders: [],
                    ordersLoading: false,
                    avatarError: false,
                    cardPosition: null,
                    dragging: false,
                    showRepeatConfirmPopup: false,
                    orderIdToRepeat: null,
                    orderToRepeat: null,
                    repeatOrderDetails: null,
                    repeatOrderLoading: false,
                    dragOffset: { x: 0, y: 0 },
                },
            };
        });
    };

    // Get call state by ID
    const getCallState = (callId) => {
        return callStates[callId] || {};
    };

    // Update call state
    const updateCallState = (callId, updates) => {
        setCallStates((prev) => ({
            ...prev,
            [callId]: { ...prev[callId], ...updates },
        }));
    };

    // Remove call from queue
    const removeCallFromQueue = (callId) => {
        stopIncomingSound();
        setCallsQueue((prev) => prev.filter((call) => call.call_id !== callId));
        setCallStates((prev) => {
            const { [callId]: _, ...rest } = prev;
            return rest;
        });
    };

    const stopIncomingSound = useCallback(() => {
        const currentAudio = incomingAudioRef.current;
        if (!currentAudio) return;

        currentAudio.pause();
        currentAudio.currentTime = 0;
        incomingAudioRef.current = null;
    }, []);

    const playIncomingSound = useCallback(() => {
        stopIncomingSound();

        if (!callNotificationSoundRef.current) return;

        const audio = new Audio(incomingCallSound);
        audio.volume = typeof volumeRef.current === "number" ? volumeRef.current : 1;
        audio.play().catch(() => {});
        incomingAudioRef.current = audio;
    }, [stopIncomingSound]);

    useEffect(() => {
        const eventName = "call-log-event";

        addEventHandler(eventName, async (payload) => {
            // console.log("Received event:", { eventName, payload });
            if (!payload || payload.type !== "CALL_LOG_RECEIVED") return;

            const incomingData = payload?.data || {};

            // console.log("Received call-log-event:", incomingData);
            if (!shouldShowCallForThisDevice(incomingData)) return;

            const customer = incomingData?.customer || {};
            const fullName = (customer?.full_name || "").trim();
            const [firstName = "", ...lastParts] = fullName ? fullName.split(" ") : [];
            const customerExists = Boolean(incomingData?.is_customer_exis);
            const customerId = customer?.id;
            const targetRestaurantId = restaurantId || incomingData?.restaurant_id || "";
            const callId = incomingData?.call_log_id ?? `call-${Date.now()}-${Math.random()}`;
            const isCustomerBanned = Boolean(incomingData?.is_customer_banned ?? incomingData?.customer?.is_banned ?? false);
            const isAddressBanned = Boolean(incomingData?.is_address_banned ?? false);

            const newCall = {
                call_id: callId,
                is_customer_exis: customerExists,
                is_customer_banned: isCustomerBanned,
                is_address_banned: isAddressBanned,
                number: incomingData?.number || "",
                line: incomingData?.line || "",
                restaurant_system_id: targetRestaurantId,
                call_log_id: incomingData?.call_log_id ?? null,
                timestamp: payload?.timestamp || "",
                customer: customerExists
                    ? {
                        ...customer,
                        first_name: customer?.first_name || firstName,
                        last_name: customer?.last_name || lastParts.join(" "),
                        number: customer?.number || incomingData?.number || "",
                        active_address: customer?.active_address || null,
                    }
                    : null,
            };

            // Initialize state for this call
            initializeCallState(callId);

            // Add call to queue
            setCallsQueue((prev) => [...prev, newCall]);

            playIncomingSound();

            if (!customerExists || !customerId || !targetRestaurantId) return;

            // Load recent orders for this customer
            updateCallState(callId, { ordersLoading: true });
            try {
                const orderRes = await getCustomerCbdOrder(targetRestaurantId, customerId, {
                    per_page: 3,
                    page: 1,
                    sort_field: "created_at",
                    sort_order: "desc",
                });

                if (orderRes?.status === "success") {
                    const orders = Array.isArray(orderRes?.data?.orders) ? orderRes.data.orders : [];
                    updateCallState(callId, { recentOrders: orders.slice(0, 3), ordersLoading: false });
                }
            } catch (error) {
                updateCallState(callId, { ordersLoading: false });
            }
        });

        return () => {
            removeEventHandler(eventName);
        };
    }, [addEventHandler, removeEventHandler, tableStatus, tableConnected, restaurantId, playIncomingSound, shouldShowCallForThisDevice]);

    const closeNotification = (callId) => {
        removeCallFromQueue(callId);
    };

    const handlePopupButtonClickCapture = (event) => {
        const target = event.target;
        if (target instanceof Element && target.closest("button")) {
            stopIncomingSound();
        }
    };

    const handleOpenRepeatConfirm = async (callId, order) => {
        const selectedOrderId = order?.id || order?.order_id;
        if (!selectedOrderId) {
            toast.error("Order id not found");
            return;
        }

        updateCallState(callId, {
            orderIdToRepeat: selectedOrderId,
            orderToRepeat: order || null,
            repeatOrderDetails: null,
            showRepeatConfirmPopup: true,
            repeatOrderLoading: true,
        });

        try {
            const res = await getSingleOrderApi(selectedOrderId);
            if (res?.data) {
                updateCallState(callId, { repeatOrderDetails: res.data, repeatOrderLoading: false });
            }
        } catch (error) {
            updateCallState(callId, { repeatOrderLoading: false });
            toast.error("Failed to load full order details");
        }
    };

    const handleCancelRepeat = (callId) => {
        updateCallState(callId, {
            showRepeatConfirmPopup: false,
            orderIdToRepeat: null,
            orderToRepeat: null,
            repeatOrderDetails: null,
            repeatOrderLoading: false,
        });
    };

    const handleConfirmRepeat = async (callId) => {
        const callState = getCallState(callId);
        const orderIdToRepeat = callState.orderIdToRepeat;

        if (!orderIdToRepeat) {
            toast.error("Order id not found");
            return;
        }

        if (!user?.restaurant_id) {
            toast.error("Restaurant id not found");
            return;
        }

        try {
            const res = await repeatOrderApi(user.restaurant_id, orderIdToRepeat);
            toast.success("Order placed successfully");
            dispatch(setOrderData(res?.data));

            handleCancelRepeat(callId);
            closeNotification(callId);

            navigate("/take-order", {
                state: { from: "cbd", orderDetailsId: res?.data?.id },
            });
        } catch (error) {
            toast.error("Failed to repeat order");
        }
    };

    const handleViewOrderDetails = async (callId, order) => {
        const orderId = order?.id || order?.order_id;

        if (!orderId) {
            toast.error("Order id not found");
            return;
        }

        dispatch(setIsLoading(true));
        try {
            const res = await getSingleOrderApi(orderId);
            if (res?.data) {
                dispatch(setOrderData(res.data));
                closeNotification(callId);
                navigate("/take-order", {
                    state: { from: "cbd", orderDetailsId: orderId },
                });
            } else {
                toast.error("Failed to load order details");
            }
        } catch (error) {
            toast.error("Failed to load order details");
        } finally {
            dispatch(setIsLoading(false));
        }
    };

    const startDrag = (event, callId) => {
        const cardRef = document.getElementById(`call-card-${callId}`);
        if (!cardRef) return;

        const rect = cardRef.getBoundingClientRect();
        const callState = getCallState(callId);
        
        updateCallState(callId, {
            dragOffset: {
                x: event.clientX - rect.left,
                y: event.clientY - rect.top,
            },
            cardPosition: { x: rect.left, y: rect.top },
            dragging: true,
        });
    };

    useEffect(() => {
        // Find if any call is being dragged
        const draggedCall = Object.entries(callStates).find(([_, state]) => state.dragging);
        if (!draggedCall) return;

        const callId = draggedCall[0];
        const callState = draggedCall[1];
        const cardRef = document.getElementById(`call-card-${callId}`);

        const handleMouseMove = (event) => {
            if (!cardRef) return;

            const cardWidth = cardRef.offsetWidth;
            const cardHeight = cardRef.offsetHeight;
            const maxX = Math.max(0, window.innerWidth - cardWidth);
            const maxY = Math.max(0, window.innerHeight - cardHeight);

            const nextX = Math.min(
                Math.max(0, event.clientX - callState.dragOffset.x),
                maxX
            );
            const nextY = Math.min(
                Math.max(0, event.clientY - callState.dragOffset.y),
                maxY
            );

            updateCallState(callId, { cardPosition: { x: nextX, y: nextY } });
        };

        const handleMouseUp = () => {
            updateCallState(callId, { dragging: false });
        };

        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);

        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
        };
    }, [callStates]);

    // Helper formatting functions (used in both old and new sections)
    const formatOrderDate = (value) => {
        if (!value) return "";
        const parsed = new Date(value);
        if (Number.isNaN(parsed.getTime())) return value;
        return parsed.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    };

    const formatCallDateTime = (value) => {
        if (!value) return "";
        const parsed = new Date(value);
        if (Number.isNaN(parsed.getTime())) return value;
        return parsed
            .toLocaleString("en-GB", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
            })
            .replace(",", "");
    };

    const formatAmount = (value) => {
        const amount = Number(value ?? 0);
        if (Number.isNaN(amount)) return value || "0.00";
        return amount.toFixed(2);
    };

    const getOrderItemPrice = (item, orderType) => {
        if (item?.is_complementary) return "Free";
        if (item?.price != null) return `£${formatAmount(item.price)}`;
        if (item?.variant?.price != null) return `£${formatAmount(item.variant.price)}`;

        if (item?.buffet_price_for === "adult" && item?.adult_price != null) {
            return `£${formatAmount(item.adult_price)}`;
        }
        if (item?.buffet_price_for && item?.kids_price != null) {
            return `£${formatAmount(item.kids_price)}`;
        }

        if (orderType === "in_house" || orderType === "table") {
            if (item?.in_price != null) return `£${formatAmount(item.in_price)}`;
            if (item?.variant?.in_price != null) return `£${formatAmount(item.variant.in_price)}`;
        }

        if (item?.out_price != null) return `£${formatAmount(item.out_price)}`;
        if (item?.variant?.out_price != null) return `£${formatAmount(item.variant.out_price)}`;
        return "N/A";
    };

    useEffect(() => {
        return () => {
            stopIncomingSound();
        };
    }, [stopIncomingSound]);




    function takeNewOrder(callData) {
        console.log("callData", callData)
        if (callData?.is_customer_exis && callData?.customer) {
            dispatch(setSelectedCustomer({...callData?.customer, call_log_id: callData?.call_log_id || null}));
            dispatch(setDeliveryInfo(callData?.customer?.active_address || {}));
        }else{
            dispatch(setSelectedCustomer({number:callData?.number || null, call_log_id: callData?.call_log_id || null}));
        }

        dispatch(setOrderData(null));
        closeNotification(callData.call_id);
        navigate("/take-order");
    }

    function goToReservation(callData) {
        const customerFullName = (callData?.customer?.full_name || "").trim();
        const fallbackName = [callData?.customer?.first_name, callData?.customer?.last_name]
            .filter(Boolean)
            .join(" ")
            .trim();

        closeNotification(callData.call_id);

        navigate("/reservation/reservation-request/create", {
            state: {
                fromCallPopup: true,
                customer: {
                    full_name: customerFullName || fallbackName || "",
                    number: callData?.customer?.number || callData?.number || "",
                    email: callData?.customer?.email || "",
                },
                call_log_id: callData?.call_log_id || null,
            },
        });
    }


    return (
        <>
            {callsQueue.map((callData, callIndex) => {
                const callId = callData.call_id;
                const callState = getCallState(callId);
                const { 
                    showCallNotification = true,
                    isMinimized = false,
                    recentOrders = [],
                    ordersLoading = false,
                    avatarError = false,
                    cardPosition = null,
                    dragging = false,
                    showRepeatConfirmPopup = false,
                    orderIdToRepeat = null,
                    orderToRepeat = null,
                    repeatOrderDetails = null,
                    repeatOrderLoading = false,
                } = callState;

                const customerName = callData.is_customer_exis
                    ? (
                        callData?.customer?.full_name ||
                        `${callData?.customer?.first_name || ""} ${callData?.customer?.last_name || ""}`.trim()
                    )
                    : "Unknown";

                const isCustomerBanned = Boolean(callData?.is_customer_banned);
                const isAddressBanned = Boolean(callData?.is_address_banned);

                let statusMessage = "";
                if (isCustomerBanned && isAddressBanned) {
                    statusMessage = "Banned Customer & Address";
                } else if (isCustomerBanned) {
                    statusMessage = "Banned Customer";
                } else if (isAddressBanned) {
                    statusMessage = "Banned Address";
                }

                const customerInitials =
                    customerName
                        .split(" ")
                        .filter(Boolean)
                        .slice(0, 2)
                        .map((part) => part[0]?.toUpperCase())
                        .join("") || "UN";

                const hasPreviousOrders = callData.is_customer_exis && recentOrders.length > 0;
                const showOrdersPanel = callData.is_customer_exis && (ordersLoading || hasPreviousOrders);
                const isPositioned = Boolean(cardPosition);

                const defaultPlacementClass = isPositioned
                    ? ""
                    : isMinimized
                      ? ""
                      : "left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2";

                const cardStyle = isPositioned
                    ? { left: `${cardPosition.x}px`, top: `${cardPosition.y}px` }
                    : isMinimized
                      ? { left: `${callIndex * 16}px`, bottom: `${callIndex * 16}px` }
                      : undefined;

                return (
                    showCallNotification && (
                        <div
                            key={callId}
                            onClickCapture={(event) => {
                                const target = event.target;
                                if (target instanceof Element && target.closest("button")) {
                                    stopIncomingSound();
                                }
                            }}
                            style={{ zIndex: 50 + callIndex }}
                            className={`fixed inset-0 flex ${
                                isMinimized
                                    ? "items-end justify-start p-3 pointer-events-none"
                                    : "items-center justify-center p-4 bg-black/55"
                            }`}
                        >
                            <div
                                id={`call-card-${callId}`}
                                style={cardStyle}
                                className={`fixed pb-6 pointer-events-auto  rounded-md border border-[#53565e] bg-[#414348] shadow-2xl text-white ${defaultPlacementClass} ${
                                    isMinimized
                                        ? "w-[250px] p-2"
                                        : showOrdersPanel
                                          ? "w-full max-w-[760px] p-3"
                                          : "w-full max-w-[360px] p-3"
                                }`}
                            >
                                 
                                <div
                                    onMouseDown={(event) => startDrag(event, callId)}
                                    className={`mb-2 flex items-center justify-between select-none ${dragging ? "cursor-grabbing" : "cursor-grab"}`}
                                >
                                    <h2 className="text-[1px] text-transparent">.</h2>
                                    <button
                                        onMouseDown={(event) => event.stopPropagation()}
                                        onClick={() => updateCallState(callId, { isMinimized: !isMinimized })}
                                        className="text-white/90 hover:text-white"
                                    >
                                        {isMinimized ? <FaUpRightAndDownLeftFromCenter /> : <TbArrowsDiagonalMinimize2 size={20} />}
                                    </button>
                                </div>
                                {!isMinimized && <h2 className=" -mt-8 mb-2 text-2xl text-center font-semibold">Incoming Call</h2>}

                                <div className={`${isMinimized ? "block" : showOrdersPanel ? "grid grid-cols-1 md:grid-cols-[310px_1fr] gap-4" : "block"}`}>
                                    <div className={showOrdersPanel && !isMinimized ? "md:pr-4 md:border-r md:border-white/15" : ""}>
                                       
                                        <div className="mb-3 flex justify-center">
                                            <div className="flex h-[94px] w-[94px] items-center justify-center overflow-hidden rounded-full bg-[#d9d9d9] text-2xl font-semibold text-[#6f6f6f]">
                                                {avatarError ? (
                                                    customerInitials
                                                ) : (
                                                    <img
                                                        src={avatarUser}
                                                        alt="Customer avatar"
                                                        className="h-full w-full object-cover"
                                                        onError={() => updateCallState(callId, { avatarError: true })}
                                                    />
                                                )}
                                            </div>
                                        </div>

                                        <h2 className="text-center text-2xl font-semibold leading-tight text-white">
                                            {customerName}
                                        </h2>

                                        <h2 className="mt-1 text-center text-2xl font-semibold leading-tight text-white">
                                            {callData?.number || "Unknown Number"}
                                        </h2>

                                        <p className="mt-2 text-center text-sm text-white/85">{formatCallDateTime(callData?.timestamp)}</p>

                                        {statusMessage && (
                                            <div className="mt-3 flex justify-center">
                                                <span className="rounded bg-red-500/10 px-3 py-1.5 text-sm font-semibold text-[#ff4d4d] border border-red-500/20">
                                                    {statusMessage}
                                                </span>
                                            </div>
                                        )}

                                        {!isMinimized && (
                                            <div className="mt-20 space-y-3">
                                                <button
                                                    onClick={() => goToReservation(callData)}
                                                    className="w-full rounded bg-[#1a8bff] py-2 text-xl font-medium text-white"
                                                >
                                                    Reservation
                                                </button>

                                                {isCustomerBanned ? (
                                                    <button
                                                        onClick={() => closeNotification(callId)}
                                                        className="w-full rounded bg-[#ff3b30] py-2 text-base font-medium text-white"
                                                    >
                                                        Close
                                                    </button>
                                                ) : (
                                                    <div className="grid grid-cols-2 gap-3">
                                                        <button onClick={() => takeNewOrder(callData)} className="w-full rounded bg-[#33cc5a] py-2 text-base font-medium text-white">
                                                            {callData.is_customer_exis ? "Take New Order" : "Take Order"}
                                                        </button>
                                                        <button
                                                            onClick={() => closeNotification(callId)}
                                                            className="w-full rounded bg-[#ff3b30] py-2 text-base font-medium text-white"
                                                        >
                                                            Close
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {isMinimized && (
                                            <button
                                                onClick={() => closeNotification(callId)}
                                                className="mt-3 w-full rounded bg-[#ff3b30] py-2 text-sm font-medium text-white"
                                            >
                                                Close
                                            </button>
                                        )}
                                    </div>
                                {!isMinimized && showOrdersPanel && (
                                <div className=" w-[400px] h-[360px] pr-1">
                                    <h3 className="mb-2 text-lg font-semibold text-white/95">Last 3 Orders</h3>

                                    {ordersLoading && (
                                        <div className="space-y-2">
                                            {[1, 2, 3].map((item) => (
                                                <div
                                                    key={item}
                                                    className="flex items-center justify-between gap-3 rounded border border-white/10 bg-[#33363a] p-3"
                                                >
                                                    <div className="animate-pulse">
                                                        <div className="h-5 w-40 rounded bg-white/20" />
                                                        <div className="mt-2 h-4 w-24 rounded bg-white/15" />
                                                        <div className="mt-2 h-4 w-28 rounded bg-white/15" />
                                                        <div className="mt-2 h-3 w-20 rounded bg-white/10" />
                                                    </div>

                                                    <div className="flex flex-col gap-2 animate-pulse">
                                                        <div className="h-9 w-24 rounded bg-white/15" />
                                                        <div className="h-9 w-36 rounded bg-white/15" />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {!ordersLoading && (
                                        <div className="space-y-2">
                                            {recentOrders.map((order, idx) => {
                                                const orderId = order?.unique_id || order?.id || order?.order_id || `#${idx + 1}`;
                                                const totalItems =
                                                    order?.total_item ?? order?.total_items ?? order?.item_count ?? 0;
                                                const totalPrice =
                                                    order?.total_price ?? order?.grand_total ?? order?.sub_total ?? 0;
                                                const date = formatOrderDate(order?.created_at || order?.date);

                                                return (
                                                    <div
                                                        key={`${orderId}-${idx}`}
                                                        className="flex items-center justify-between gap-3 rounded border border-white/10 bg-[#33363a] p-3"
                                                    >
                                                        <div>
                                                            <div className="text-base font-semibold text-[#ffcf33]">Order ID: {orderId}</div>
                                                            <div className="text-sm text-white/90">Total Item: {totalItems}</div>
                                                            <div className="text-sm text-white/90">
                                                                Total Price: £ {formatAmount(totalPrice)}
                                                            </div>
                                                            <div className="text-xs text-white/70">{date}</div>
                                                        </div>

                                                        <div className="flex flex-col gap-2">
                                                            <button
                                                                onClick={() => handleViewOrderDetails(callId, order)}
                                                                className="rounded bg-[#1a8bff] px-3 py-2 text-sm font-medium text-white"
                                                            >
                                                                View Details
                                                            </button>
                                                            {!isCustomerBanned && (
                                                                <button
                                                                    onClick={() => handleOpenRepeatConfirm(callId, order)}
                                                                    className="rounded bg-[#33cc5a] px-3 py-2 text-sm font-medium text-white"
                                                                >
                                                                    Same as another order
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}
                                </div>
                        </div>

                    {showRepeatConfirmPopup && (
                        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4">
                            <div className="w-full max-w-[760px] rounded-md border border-[#53565e] bg-[#414348] p-5 text-white shadow-2xl">
                                <p className="text-center text-lg font-semibold">
                                    Do you wanted to make same order like this one ?
                                </p>

                                <div className="mt-4 rounded border border-white/10 bg-[#33363a] p-3">
                                    <div className="grid grid-cols-1 gap-2 text-sm text-white/90 md:grid-cols-2">
                                        <div className="font-semibold text-[#ffcf33]">
                                            Order ID: {repeatOrderDetails?.id || orderToRepeat?.unique_id || orderToRepeat?.id || orderToRepeat?.order_id || "N/A"}
                                        </div>
                                        <div>
                                            Customer: {repeatOrderDetails?.customer?.full_name || customerName || "N/A"}
                                        </div>
                                        <div>
                                            Phone: {repeatOrderDetails?.customer?.number || callData?.number || "N/A"}
                                        </div>
                                        <div>
                                            Type: {repeatOrderDetails?.order_type || "N/A"}
                                        </div>
                                        <div>
                                            Date: {formatOrderDate(repeatOrderDetails?.created_at || orderToRepeat?.created_at || orderToRepeat?.date) || "N/A"}
                                        </div>
                                        <div>
                                            Total Price: £ {formatAmount(repeatOrderDetails?.total_price ?? orderToRepeat?.total_price ?? orderToRepeat?.grand_total ?? orderToRepeat?.sub_total ?? 0)}
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-3">
                                    <h3 className="mb-2 text-sm font-semibold text-white/95">Items</h3>

                                    {repeatOrderLoading ? (
                                        <div className="space-y-2">
                                            {[1, 2, 3].map((item) => (
                                                <div
                                                    key={item}
                                                    className="animate-pulse rounded border border-white/10 bg-[#33363a] p-3"
                                                >
                                                    <div className="h-4 w-48 rounded bg-white/20" />
                                                    <div className="mt-2 h-3 w-32 rounded bg-white/10" />
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="max-h-[260px] space-y-2 overflow-auto pr-1">
                                            {(repeatOrderDetails?.items || []).length > 0 ? (
                                                (repeatOrderDetails?.items || []).map((item, idx) => (
                                                    <div
                                                        key={`${item?.order_item_id || item?.id || idx}`}
                                                        className="flex items-center justify-between gap-3 rounded border border-white/10 bg-[#33363a] p-3"
                                                    >
                                                        <div>
                                                            <div className="text-sm font-semibold text-white">{item?.name || "N/A"}</div>
                                                            <div className="text-xs text-white/75">
                                                                {item?.variant?.name ? `(${item.variant.name}) ` : ""}
                                                                {item?.size ? `(${item.size}) ` : ""}
                                                                {item?.buffet_price_for ? `(${item.buffet_price_for === "adult" ? "Adult" : "Kids"})` : ""}
                                                            </div>
                                                        </div>

                                                        <div className="text-right">
                                                            <div className="text-xs text-white/85">x{item?.quantity ?? 0}</div>
                                                            <div className="text-sm font-medium text-white/95">
                                                                {getOrderItemPrice(item, repeatOrderDetails?.order_type)}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="rounded border border-white/10 bg-[#33363a] p-3 text-sm text-white/70">
                                                    No item details available.
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div className="mt-5 flex gap-3">
                                    <button
                                        onClick={() => handleConfirmRepeat(callId)}
                                        className="w-full rounded bg-[#33cc5a] py-2 text-sm font-medium text-white"
                                    >
                                        Yes
                                    </button>
                                    <button
                                        onClick={() => handleCancelRepeat(callId)}
                                        className="w-full rounded bg-[#ff3b30] py-2 text-sm font-medium text-white"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                    </div>
                    )
                )
            })}
        </>
    );
};

export default Call;
